package ai.wagemix.web.screens.picker

import ai.wagemix.logic.picker.WMOccupationLimit
import ai.wagemix.logic.picker.WMOccupationPickerRow
import ai.wagemix.logic.picker.WMOccupationPickerPresenter
import ai.wagemix.logic.picker.WMOccupationPickerPresenter.Action
import ai.wagemix.logic.picker.WMOccupationPickerPresenter.Event
import ai.wagemix.web.components.WMButton
import ai.wagemix.web.components.WMButtonKind
import ai.wagemix.web.components.WMButtonSize
import ai.wagemix.web.components.WMDialog
import ai.wagemix.web.components.WMSidePanelDialogVariant
import ai.wagemix.web.components.WMDialogButtonStyle
import ai.wagemix.web.components.WMDialogFillStyle
import ai.wagemix.web.components.WMIconGlyph
import ai.wagemix.web.components.WMNotice
import ai.wagemix.web.components.WMNoticeKind
import ai.wagemix.web.components.WMTextField
import ai.wagemix.web.components.WMTreeRow
import ai.wagemix.web.components.WMTreeRowPresentation
import ai.wagemix.web.components.of
import ai.wagemix.web.screens.WMWebStrings
import ai.wagemix.web.theme.WMBodySmallTextStyle
import ai.wagemix.web.theme.WMCardStackGapVar
import ai.wagemix.web.theme.WMLabelTextStyle
import ai.wagemix.web.theme.WMTightGapVar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.State
import androidx.compose.runtime.key
import com.fsryan.foundation.web.components.FSLoadingIndicator
import com.fsryan.foundation.web.screens.FSWebScreen
import com.fsryan.foundation.web.theme.fs
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.ariaLabel
import com.varabyte.kobweb.compose.ui.modifiers.overflow
import com.varabyte.kobweb.compose.ui.modifiers.role
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette

/**
 * Penpot `Browse occupations` (side sheet on desktop, bottom sheet on phones), rendered as a
 * sheet-sized dialog: search, the taxonomy tree, the selection count and [Add to mix].
 */
@Composable
fun <E: Event, A: Action, PR: WMOccupationPickerPresenter<E, A>, VM: WMBaseOccupationPickerWebViewModel<E, A, PR>> WMOccupationPickerSheet(
    createViewModel: () -> VM,
    strings: WMWebStrings,
    onClose: () -> Unit
) {
    FSWebScreen(createViewModel = createViewModel) { vm ->
        WMOccupationPickerLayout(
            strings = strings,
            loadingState = vm.loadingState,
            rowsState = vm.rowsState,
            queryState = vm.queryState,
            selectionCountState = vm.selectionCountState,
            taxonomyErrorState = vm.taxonomyErrorState,
            occupationLimitState = vm.occupationLimitState,
            onQueryChange = vm::userChangedQuery,
            onToggleExpanded = vm::userToggledExpanded,
            onToggleSelected = vm::userToggledSelected,
            onClearSelection = vm::userClearedSelection,
            onAddSelection = vm::userRequestedAddSelection,
            onClose = onClose
        )
    }
}

@Composable
fun WMOccupationPickerLayout(
    strings: WMWebStrings,
    loadingState: State<Boolean>,
    rowsState: State<List<WMOccupationPickerRow>>,
    queryState: State<String>,
    selectionCountState: State<Int>,
    taxonomyErrorState: State<Boolean>,
    occupationLimitState: State<WMOccupationLimit?>,
    onQueryChange: (String) -> Unit,
    onToggleExpanded: (Int) -> Unit,
    onToggleSelected: (Int) -> Unit,
    onClearSelection: () -> Unit,
    onAddSelection: () -> Unit,
    onClose: () -> Unit
) {
    val b = strings.picker
    val colors = ColorMode.current.toPalette().fs
    val count = selectionCountState.value
    WMDialog(
        title = b.title,
        onDismiss = onClose,
        variant = WMSidePanelDialogVariant,
        actions = {
            SpanText("$count ${b.selected}", WMLabelTextStyle.toModifier().color(colors.onSurfaceVariant))
            if (count > 0) {
                WMButton(b.clear, kind = WMButtonKind.Ghost, size = WMButtonSize.Sm, onClick = onClearSelection)
            }
            WMButton(strings.common.cancel, kind = WMButtonKind.Ghost, modifier = WMDialogButtonStyle.toModifier(), onClick = onClose)
            WMButton(b.addToMix, modifier = WMDialogButtonStyle.toModifier(), enabled = count > 0, leadingIcon = WMIconGlyph.Plus, onClick = onAddSelection)
        }
    ) {
        // The sheet's height is fixed by the viewport (WMSidePanelDialogVariant); this column takes what
        // the title and the actions leave, and only the tree scrolls, so filtering never resizes it.
        Column(modifier = WMDialogFillStyle.toModifier().gap(WMCardStackGapVar.value())) {
            // The query is sent to analytics (occupation_searched), so the field says not to enter anything personal.
            Column(modifier = Modifier.fillMaxWidth().gap(WMTightGapVar.value())) {
                WMTextField(
                    queryState.value,
                    onTextChange = onQueryChange,
                    placeholder = b.searchPlaceholder,
                    leadingIcon = WMIconGlyph.Search,
                )
                SpanText(b.searchNotice, WMBodySmallTextStyle.toModifier().color(colors.onSurfaceVariant))
            }
            if (taxonomyErrorState.value) {
                WMNotice(b.loadFailed, kind = WMNoticeKind.Error)
            }
            // Placeholder until the Penpot design for this notice (paid-feature plan W7's design pass).
            occupationLimitState.value?.let { limit ->
                WMNotice(b.tooManyOccupationsTitle(limit.limit), kind = WMNoticeKind.Warning, message = b.tooManyOccupationsMessage(limit.attempted))
            }
            if (loadingState.value) {
                FSLoadingIndicator(strings.common.loading)
            }
            val rows = rowsState.value
            Column(modifier = WMOccupationPickerTreeStyle.toModifier().role("tree").ariaLabel(b.title)) {
                if (rows.isEmpty() && queryState.value.isNotBlank() && !loadingState.value) {
                    SpanText(b.noResults, WMBodySmallTextStyle.toModifier().color(colors.onSurfaceVariant))
                }
                rows.forEach { row ->
                    key(row.occupationId) {
                        WMTreeRow(
                            row = WMTreeRowPresentation.of(
                                id = row.occupationId,
                                name = row.name,
                                depth = row.depth,
                                descendantCount = row.descendantCount,
                                countsMatches = row.countsMatches,
                                expandable = row.hasChildren,
                                expanded = row.expanded,
                                checked = row.selected,
                                inMix = row.inMix,
                                parentSelected = row.parentSelected,
                            ),
                            strings = b.row,
                            onToggleExpanded = { onToggleExpanded(row.occupationId) },
                            onCheckedChange = { onToggleSelected(row.occupationId) }
                        )
                    }
                }
            }
        }
    }
}

/** The tree takes the rest of the sheet's fixed height and scrolls inside it. */
val WMOccupationPickerTreeStyle = CssStyle.base {
    Modifier.fillMaxWidth().overflow(Overflow.Auto).styleModifier {
        property("flex", "1 1 0")
        property("min-height", "0")
    }
}
